/*
 * Copyright (c) CIRROM 2025.
 */

import React from "react";
import styled from "@emotion/styled";

import { green } from "@mui/material/colors";

import {
  Avatar,
  Badge,
  Divider,
  Tooltip,
  Menu,
  MenuItem,
  IconButton as MuiIconButton,
  Drawer,
} from "@mui/material";
import { spacing } from "@mui/system";
import { MiraTheme } from "../../theme/theme";
import { useAuth } from "../../../../hooks/useAuth";
import { ThemeSelector } from "../../theme/ThemeSelector";
import { useNavigate } from "react-router-dom";
import { setPreferredUI } from "../../../../utils/uiPreference";

const IconButton = styled(MuiIconButton)`
  ${spacing};

  &:hover {
    background-color: transparent;
  }
`;

const AvatarBadge = styled(Badge)<{ theme?: MiraTheme }>`
  margin-right: ${(props) => props.theme.spacing(1)};
  span {
    background-color: ${() => green[400]};
    border: 2px solid ${(props) => props.theme.palette.background.paper};
    height: 12px;
    width: 12px;
    border-radius: 50%;
  }
`;

export function NavbarUserDropdown() {
  const [anchorMenu, setAnchorMenu] = React.useState<any>(null);
  const [themeDrawerOpen, setThemeDrawerOpen] = React.useState(false);

  const auth = useAuth();
  const navigate = useNavigate();
  const toggleMenu = (event: React.SyntheticEvent) => {
    setAnchorMenu(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorMenu(null);
  };

  const handleThemeClick = () => {
    closeMenu();
    setThemeDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setThemeDrawerOpen(false);
  };

  const handleSignOut = () => {
    auth.logout();
  };

  const handleUISwitch = () => {
    setPreferredUI("app");
    navigate("/app/dashboard");
  };

  const handleHelpClick = async () => {
    window.open(`mailto:support@cirrom.com?subject=Support Required`, "_blank");
    closeMenu();
  };

  const navigateAndClose = (url: string) => {
    navigate(url);
    closeMenu();
  };

  return (
    <React.Fragment>
      <Tooltip title="Account">
        <IconButton
          aria-owns={anchorMenu ? "menu-appbar" : undefined}
          aria-haspopup="true"
          onClick={toggleMenu}
          color="inherit"
          p={0}
          mx={1}
        >
          <AvatarBadge
            overlap="circular"
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            variant="dot"
          >
            {!!auth.currentUser && <Avatar alt={auth.currentUser} src={null} />}
          </AvatarBadge>
        </IconButton>
      </Tooltip>
      <Menu
        id="menu-appbar"
        anchorEl={anchorMenu}
        open={Boolean(anchorMenu)}
        onClose={closeMenu}
      >
        <MenuItem onClick={handleThemeClick}>Theme</MenuItem>
        <MenuItem onClick={handleUISwitch}>Switch to old UI</MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            navigateAndClose("/product/configure/users");
          }}
        >
          Users
        </MenuItem>
        <MenuItem
          onClick={() => navigateAndClose("/product/configure/sources")}
        >
          Data Sources
        </MenuItem>
        <MenuItem
          onClick={() => navigateAndClose("/product/configure/secrets")}
        >
          Secrets & Preferences
        </MenuItem>
        <MenuItem
          onClick={() =>
            navigateAndClose("/product/configure/job-title-config")
          }
        >
          Job Title Abbreviations
        </MenuItem>
        <MenuItem
          onClick={() => navigateAndClose("/product/configure/support")}
        >
          Support
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleHelpClick}>Help</MenuItem>
        <MenuItem onClick={handleSignOut}>Sign out</MenuItem>
      </Menu>
      <Drawer anchor="right" open={themeDrawerOpen} onClose={handleDrawerClose}>
        <ThemeSelector />
      </Drawer>
    </React.Fragment>
  );
}
